/**
 * The reseller portal's accent — the logo's navy.
 *
 * History: this ramp was teal until 2026-09-08, when the product owner asked
 * for the portal's colouring to sit closer to the XOP Networks mark. The
 * mark (`/xop-logo.svg`) is two colours — navy #263961 and lime #a4c21e —
 * so the accent now IS that navy (700 verbatim, the ramp built around it)
 * and lime stays the secondary it already was. The differentiation argument
 * below is kept for the record; the owner's call outranks it.
 *
 * ── Why a second accent at all ────────────────────────────────────────
 * The sign-in screen is a deliberate near-copy of the customer support
 * portal's (`xop-edge-customer/apps/portal-web/src/pages/auth/`): same white
 * top bar, same nameplate, same dot-globe hero, same dark glass card, same
 * contact footer. Two portals that look identical are two portals people
 * sign in to by mistake, so exactly one visual variable is turned: the
 * accent. Support is XOP brand blue; XOP Reseller was teal, and is now the
 * logo's own navy — a deeper, greyer blue than support's.
 *
 * ── Why teal (superseded, kept for the record) ────────────────────────
 * XOP's mark is navy + lime. Teal sits between them on the hue wheel, so it
 * still reads as the same family (it is what navy looks like with the lime's
 * chroma pushed into it) while being unmistakably not-blue at a glance —
 * even in a browser-tab-sized thumbnail, which is the real test. The two
 * alternatives were both worse: lime alone cannot carry a CTA (white text
 * fails on it, dark text on lime reads as a warning banner), and the support
 * portal *already* spends lime on its markers and its lower glow, so a lime
 * hero would have differentiated less, not more. Lime is kept here too, but
 * only where the reference keeps it — the globe's network nodes and packets
 * — which is precisely the shared family signature worth keeping.
 *
 * ── Why a `palette.accent`, not `palette.primary` ─────────────────────
 * `primary` stays XOP navy: it is the *company's* colour and the rest of the
 * app (and every sibling repo's theme, which `theme.ts` is a copy of) is
 * built on it. `accent` is the *portal's* colour. Keeping them separate means
 * the sign-in can carry its own ramp without silently re-skinning every future button,
 * and means re-skinning the portal later is one edit to this table.
 *
 * Every accent colour in the app comes from here — components read
 * `theme.palette.accent.*`, never a literal.
 */

/** A 200→800 ramp plus the two derived values every consumer needs.
 * Numbered like the support portal's `--color-brand-*` scale so the two
 * designs' colour roles line up one-for-one when they are read side by
 * side (their `brand-500` is this `500`, and so on). */
export interface AccentScale {
  /** Palest tint — dot fields, hairlines on dark. */
  200: string;
  /** Continent dots and graticule on the lit globe. */
  300: string;
  /** The bright end of the ramp — limb light, dark-mode links. */
  400: string;
  /** The accent proper — focus rings, light-mode links. */
  500: string;
  /** CTA gradient start. Deep enough that white clears 4.5:1 on it — the
   * button is a gradient, so its LIGHTEST stop is the one that has to pass.
   * Pinned by accent.test.ts. */
  600: string;
  /** CTA gradient middle. */
  700: string;
  /** CTA gradient end, deepest ink. */
  800: string;
  /** Text that sits on 600–800 (the CTA label). */
  contrastText: string;
  /** Translucent wash for focus halos and selected surfaces. */
  soft: string;
}

/* Light theme. The ramp is used at its darker end here, because everything
 * it colours (links, focus rings, the CTA) sits on a near-white surface. */
export const ACCENT_LIGHT: AccentScale = {
  200: "#cfd9ec",
  300: "#a6b7d9",
  400: "#7691c2",
  500: "#4c6ba6",
  600: "#37538b",
  700: "#263961", // the logo's navy, exactly
  800: "#1b2a49",
  contrastText: "#ffffff",
  soft: "rgba(76,107,166,0.14)",
};

/* Dark theme. Same hue, lifted a step or two: on near-black the mid-tones
 * of the light ramp go muddy, so links and rings move up to 400/300 while
 * the CTA keeps the deeper end so its white label still reads. */
export const ACCENT_DARK: AccentScale = {
  200: "#dde5f4",
  300: "#b8c6e4",
  400: "#8ea6d3",
  500: "#6484bd",
  // The CTA end sits a notch brighter than the light ramp's: on near-black
  // the logo navy itself disappears into the field, so the button gradient
  // starts where white still clears AA and the blue still reads as blue.
  600: "#4a6db0",
  700: "#3a5896",
  800: "#26407a",
  contrastText: "#ffffff",
  soft: "rgba(142,166,211,0.20)",
};

declare module "@mui/material/styles" {
  interface Palette {
    /** This portal's own accent — see the file header. */
    accent: AccentScale;
  }
  interface PaletteOptions {
    accent?: AccentScale;
  }
}
